import { maxBy, oldestDate, sumBy } from 'utils/billing';
import { parseCurrency } from 'utils/parsers';
import { formatadores } from 'utils/validators';

// Colunas das seis telas do menu Cobranca, definidas uma unica vez. Cada tela
// escolhe quais quer e em que ordem; o que a coluna mostra e como ela se
// comporta na linha agrupada fica aqui.
//
// A nomenclatura de referencia e a da COBRANCA, que e a tela ja em producao:
// campo que significa a mesma coisa usa o mesmo `ref` em todas as telas
// (`seller`, `valor_parcelas`, `dias_atraso`), mesmo onde o rascunho original
// tinha um nome proprio. Ao acrescentar coluna nova, siga o mesmo caminho.

const phoneCell =
  (ref) =>
  ({ row }) => {
    const numero = row?.[ref];
    if (!numero) {
      return '-';
    }
    const numeroLimpo = `${numero}`.replace(/\D/g, '');
    return (
      <a
        href={`https://wa.me/55${numeroLimpo}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: 'blue',
          textDecoration: 'underline',
          cursor: 'pointer',
        }}
      >
        {numero}
      </a>
    );
  };

// Soma de um campo numerico das parcelas, para a linha do contrato. Se nenhuma
// parcela trouxer o valor, a celula fica em branco em vez de mostrar R$ 0,00.
const sumOrBlank = (rows, field) => {
  const valores = (rows || [])
    .map((row) => row?.[field])
    .filter((valor) => Number.isFinite(valor));
  return valores.length
    ? parseCurrency(valores.reduce((total, valor) => total + valor, 0))
    : null;
};

// Data guardada em ISO na linha e formatada so aqui: a ordenacao compara a
// string crua, e 'DD/MM/AAAA' ordenaria pelo dia.
const dateCell =
  (ref) =>
  ({ row }) =>
    formatadores.TIMESTAMP_TO_BR(row?.[ref]);

export const BILLING_COLUMNS = {
  id: { title: 'Id', ref: 'id', groupLevel: true, sortable: true },
  contrato: { title: 'Contrato', ref: 'contrato', groupLevel: true },
  data_emissao: {
    title: 'Data',
    ref: 'data_emissao',
    sortable: true,
    groupLevel: true,
    renderCell: dateCell('data_emissao'),
  },
  banco: { title: 'Banco', ref: 'banco', sortable: true, groupLevel: true },
  ope: { title: 'Operação', ref: 'ope', groupLevel: true },
  distributor: {
    title: 'Distribuidora',
    ref: 'distributor',
    sortable: true,
    groupLevel: true,
  },
  integrator: { title: 'Integrador', ref: 'integrator', groupLevel: true },
  seller: {
    title: 'Vendedor',
    ref: 'seller',
    sortable: true,
    groupLevel: true,
  },
  seller_phone: {
    title: 'Telefone',
    ref: 'seller_phone',
    groupLevel: true,
    renderCell: phoneCell('seller_phone'),
  },
  customer: {
    title: 'Cliente',
    ref: 'customer',
    sortable: true,
    groupLevel: true,
  },
  telefone: {
    title: 'Telefone',
    ref: 'telefone',
    groupLevel: true,
    renderCell: phoneCell('telefone'),
  },
  score: { title: 'Score', ref: 'score', sortable: true, groupLevel: true },
  // CON e o valor total do contrato; o cabecalho e abreviado por largura.
  con: { title: 'CON', ref: 'con', groupLevel: true },
  value: {
    title: 'Valor',
    ref: 'value',
    // Na linha do contrato, a soma do `valor` das parcelas.
    renderGroupCell: ({ rows }) => sumOrBlank(rows, 'value_num'),
  },
  valor_liquido: { title: 'LIQ', ref: 'valor_liquido' },
  parcela: {
    title: 'Parcela',
    ref: 'parcela',
    renderGroupCell: ({ rows }) => `${rows.length} parcelas`,
  },
  valor_parcelas: {
    title: 'PMT',
    ref: 'valor_parcelas',
    sortable: true,
    renderGroupCell: ({ rows }) =>
      parseCurrency(sumBy(rows, 'valor_parcela_num')),
  },
  saldo: {
    title: 'Saldo',
    ref: 'saldo',
    sortable: true,
    // So do contrato: o `valor_saldo` dele menos o valor dos titulos baixados
    // (montado em `contractFields`, utils/billing.js). As parcelas expandidas
    // ficam em branco. Sem o `valor_saldo`, a celula do contrato tambem.
    groupLevel: true,
  },
  prazo: { title: 'Prazo', ref: 'prazo' },
  data_vencimento: {
    title: 'Vencimento',
    ref: 'data_vencimento',
    sortable: true,
    renderCell: dateCell('data_vencimento'),
    renderGroupCell: ({ rows }) =>
      formatadores.TIMESTAMP_TO_BR(oldestDate(rows, 'data_vencimento')),
  },
  dias_atraso: {
    title: 'ATR',
    ref: 'dias_atraso',
    sortable: true,
    // Na linha do contrato vale o `maior_atraso` que o backend calcula — o
    // mesmo campo que recorta a esteira na query, para o numero da tela
    // nunca discordar do filtro que a trouxe.
    renderGroupCell: ({ row, rows }) =>
      Number.isFinite(row?.maior_atraso)
        ? row.maior_atraso
        : maxBy(rows, 'dias_atraso'),
  },
  ultima_tentativa: {
    title: 'Última tentativa',
    ref: 'ultima_tentativa',
    sortable: true,
    groupLevel: true,
    renderCell: dateCell('ultima_tentativa'),
  },
  // AÇÃO fica so como cabecalho; cada tela sobrescreve a celula com o modal
  // que e dela — hoje a Recuperacao (notificar) e a Notificacao (renegociar e
  // executar). TAG nao e usada por nenhuma tela no momento.
  acao: { title: 'AÇÃO', ref: 'renegociar', groupLevel: true },
  tag: { title: 'TAG', ref: 'renegociar', groupLevel: true },
  // Ja o Historico e o mesmo nas seis telas: a celula vem pronta de
  // `buildHistoryOverride` (components/Billing/historyAction).
  historico: {
    title: 'Histórico',
    ref: 'data',
    align: 'center',
    sortable: true,
  },
};

// A coluna Distribuidora so existe para quem enxerga mais de um distribuidor.
export const canSeeDistributor = (user) =>
  !!user?.administrador ||
  !!(user?.distribuidor && user?.distribuidores?.length > 0);

// `keys` define quais colunas e em que ordem; `overrides` acrescenta ou troca
// propriedades de uma delas — e por ai que a Cobranca injeta os renderCell que
// abrem modais, sem duplicar a definicao da coluna.
export const buildBillingColumns = (keys, { user, overrides = {} } = {}) =>
  (keys || [])
    .filter((key) => key !== 'distributor' || canSeeDistributor(user))
    .map((key) => ({ ...BILLING_COLUMNS[key], ...(overrides[key] || {}) }))
    .filter((column) => !!column.ref);
